import { useParams, Navigate } from "react-router-dom";
import { Phone, ArrowRight, CheckCircle2, MapPin } from "lucide-react";
import SEO from "@/components/SEO";
import { businessSchema, faqSchema, serviceSchema } from "@/data/schema";
import { townsData } from "@/data/towns";
import { Reveal, MaskedLine } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import FAQ from "@/components/FAQ";
import CTABand from "@/components/CTABand";

export default function TownPage() {
  const { slug } = useParams();
  const town = townsData[slug];
  if (!town) return <Navigate to="/" replace />;

  const origin = window.location.origin;
  const townLabel = `${town.name}, LA`;

  return (
    <div data-testid={`town-page-${town.slug}`}>
      <SEO
        title={town.title}
        description={town.description}
        path={`/service-areas/${town.slug}`}
        jsonld={[
          serviceSchema(origin, {
            name: `HVAC Repair and Installation in ${townLabel}`,
            description: `24/7 residential and commercial HVAC repair, AC installation, and mechanical service in ${townLabel} and surrounding ${town.parish}.`,
            area: townLabel,
          }),
          faqSchema(town.faqs),
        ]}
      />

      {/* HERO */}
      <section className="relative bg-navy-deep overflow-hidden grain-overlay">
        <div className="absolute inset-0 hero-grid-bg" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-20 lg:py-32">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-frost mb-5">
            <MaskedLine delay={0.1}>{town.parish} · Central Louisiana · ZIP {town.zip}</MaskedLine>
          </p>
          <h1 className="font-display text-5xl sm:text-7xl font-black uppercase tracking-tight leading-[0.92] text-white">
            <MaskedLine delay={0.2}>HVAC Repair &</MaskedLine>
            <MaskedLine delay={0.35}>
              Installation In <span className="text-ember">{town.name}, LA</span>
            </MaskedLine>
          </h1>
          <Reveal delay={0.5} className="mt-7 max-w-2xl text-base sm:text-lg text-slate-300 leading-relaxed">
            <p>{town.heroIntro}</p>
          </Reveal>
          <Reveal delay={0.65} className="mt-10 flex flex-col sm:flex-row gap-4">
            <a
              data-testid={`town-${town.slug}-call-btn`}
              href="tel:3183122112"
              className="inline-flex items-center justify-center gap-3 bg-ember hover:bg-ember-dark text-navy-deep font-bold px-8 py-5 rounded-sm transition-all hover:-translate-y-1 active:scale-95"
            >
              <Phone className="h-5 w-5" />
              Call (318) 312-2112
            </a>
            <a
              data-testid={`town-${town.slug}-quote-btn`}
              href="/contact"
              className="inline-flex items-center justify-center gap-3 border border-frost/60 text-frost hover:bg-frost hover:text-navy-deep font-bold px-8 py-5 rounded-sm transition-all hover:-translate-y-1 active:scale-95"
            >
              Request an Estimate
              <ArrowRight className="h-5 w-5" />
            </a>
          </Reveal>
        </div>
      </section>

      <Marquee items={[`${town.name} LA`, "24/7 Emergency Dispatch", "AC Repair", "Heating Service", "System Installation", "Commercial Mechanical", "(318) 312-2112"]} dark />

      {/* REPAIR */}
      <article className="bg-white py-20 lg:py-28" data-testid={`town-${town.slug}-repair-section`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-ember mb-4">Emergency & Same-Day</p>
            <h2 className="font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight leading-[0.95] text-navy">
              24/7 HVAC Repair In {town.name}
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7 space-y-5 text-slate-600 leading-relaxed">
            {town.repairCopy.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
            <ul className="space-y-3 pt-2">
              {town.repairPoints.map((point) => (
                <li key={point} className="flex items-start gap-3 text-sm font-medium text-navy">
                  <CheckCircle2 className="h-5 w-5 text-ember shrink-0 mt-0.5" />
                  {point}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </article>

      {/* INSTALLATION */}
      <article className="bg-slate-50 py-20 lg:py-28" data-testid={`town-${town.slug}-install-section`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-frost-dark mb-4">Replacement & New Systems</p>
            <h2 className="font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight leading-[0.95] text-navy">
              AC Installation In {town.name}, LA
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7 space-y-5 text-slate-600 leading-relaxed">
            <p>{town.installCopy}</p>
            <ul className="space-y-3 pt-2">
              {town.installPoints.map((point) => (
                <li key={point} className="flex items-start gap-3 text-sm font-medium text-navy">
                  <CheckCircle2 className="h-5 w-5 text-frost-dark shrink-0 mt-0.5" />
                  {point}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </article>

      {/* COMMERCIAL */}
      <article className="bg-navy-deep py-20 lg:py-28 relative overflow-hidden grain-overlay" data-testid={`town-${town.slug}-commercial-section`}>
        <div className="hero-grid-bg absolute inset-0" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-ember mb-4">For Business</p>
            <h2 className="font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight leading-[0.95] text-white">
              Commercial Mechanical HVAC In {town.name}
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7 space-y-5 text-slate-300 leading-relaxed">
            <p>{town.commercialCopy}</p>
            <div className="flex flex-col sm:flex-row gap-4 pt-3">
              <a
                data-testid={`town-${town.slug}-commercial-call`}
                href="tel:3183122112"
                className="inline-flex items-center justify-center gap-2 bg-ember hover:bg-ember-dark text-navy-deep font-bold px-7 py-3.5 rounded-sm transition-all hover:-translate-y-0.5 active:scale-95"
              >
                <Phone className="h-4 w-4" />
                Call (318) 312-2112
              </a>
            </div>
          </Reveal>
        </div>
      </article>

      {/* FAQ + MAP */}
      <section className="bg-white py-20 lg:py-28" data-testid={`town-${town.slug}-faq-section`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-ember mb-4">Local Questions</p>
              <h2 className="font-display text-4xl font-extrabold uppercase tracking-tight leading-[0.95] text-navy mb-8">
                {town.name} HVAC FAQs
              </h2>
            </Reveal>
            <FAQ items={town.faqs} />
          </div>
          <Reveal delay={0.15}>
            <div
              data-testid={`town-${town.slug}-map`}
              className="relative aspect-[4/3] rounded-md overflow-hidden border border-slate-200 shadow-[0_20px_60px_rgba(15,9,71,0.15)]"
            >
              <iframe
                title={`Map of ${town.name}, Louisiana HVAC service area`}
                src={`https://maps.google.com/maps?q=${town.mapQuery}&t=&z=11&ie=UTF8&iwloc=&output=embed`}
                className="absolute inset-0 w-full h-full"
                style={{ border: 0 }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <p className="mt-3 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
              <MapPin className="h-3.5 w-3.5 text-ember" />
              {town.name}, LA {town.zip} — dispatched daily from Trout HQ
            </p>
          </Reveal>
        </div>
      </section>

      <CTABand
        title={`${town.name}'s 24/7 HVAC Line Is Open`}
        sub={`Homes and businesses across ${town.name} and ${town.parish} get a live answer and a fast dispatch — any hour, any day.`}
      />
    </div>
  );
}
